import { useEffect, useRef, useState } from "react";
import { Printer, X, Share2, Download, Eye, EyeOff, Users, Loader2, ClipboardCheck, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { TRY, NUM } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stat2 } from "@/components/pos/PosBits";
import PdfPagesView from "@/components/PdfPagesView";
import { printPdf } from "@/lib/quotePdf";
import { buildReceiptPdf, receiptBelgeNo, receiptFilename, saleProfit } from "@/lib/receipt";
import { ParticipationInfo } from "@/components/pos/ParticipationInfo";

// Satış fişi önizleme: ekranda görünen = indirilen = yazdırılan PDF (vektörel). Sütun seçimi, (yönetici) kâr/marj & katılım.
// Tüm görünüm seçenekleri (ürün kodu, marka adı, iskonto, logolar, tek sayfa, KDV notu) HER AÇILIŞTA KAPALI başlar; yalnız o önizlemede açılır.
const DEF_COLS = { code: false, brand: false, discount: false, logos: false, single: false, vat: false };
const defaultVatText = (sale) => ((sale?.items || []).some((it) => (Number(it.kdv_rate) || 0) > 0) ? "Tutarlara KDV dahildir" : "KDV uygulanmamıştır");

export function ReceiptPreviewDialog({ sale, isAdmin, onClose, onSendPrep, onToQuote }) {
  const [rcols, setRcols] = useState(DEF_COLS);
  const [vatText, setVatText] = useState(() => defaultVatText(sale));
  const [showProfit, setShowProfit] = useState(false);
  const [showPart, setShowPart] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pdfRef = useRef(null);
  const width = Math.min(720, (typeof window !== "undefined" ? window.innerWidth : 800) - 72);
  useEffect(() => { setShowProfit(false); setShowPart(false); setRcols(DEF_COLS); setVatText(defaultVatText(sale)); }, [sale?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sale) { setPdfData(null); pdfRef.current = null; return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const pdf = await buildReceiptPdf(sale, { cols: rcols, singlePage: rcols.single, vatNote: rcols.vat, vatNoteText: vatText });
        if (cancelled) return;
        pdfRef.current = pdf;
        setPdfData(pdf.output("arraybuffer"));
      } catch (e) { console.error(e); if (!cancelled) toast.error("Fiş önizlemesi oluşturulamadı"); }
      finally { if (!cancelled) setBusy(false); }
    }, rcols.vat ? 250 : 80);
    return () => { cancelled = true; clearTimeout(t); };
  }, [sale, rcols, vatText]);

  const ready = () => { if (!pdfRef.current) { toast.error("Fiş henüz hazır değil"); return null; } return pdfRef.current; };
  const doDownload = () => { const pdf = ready(); if (!pdf) return; pdf.save(receiptFilename(sale)); toast.success("Fiş indirildi"); };
  const doPrint = () => { const pdf = ready(); if (!pdf) return; printPdf(pdf, receiptFilename(sale).replace(/\.pdf$/, ""), { asFileName: true }); };
  const doShare = async () => {
    const pdf = ready(); if (!pdf) return;
    try {
      const blob = pdf.output("blob");
      const file = new File([blob], receiptFilename(sale), { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Satış Fişi", text: `${COMPANY.name} — Satış Fişi` });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fiş PDF olarak indirildi — dilediğiniz uygulamadan paylaşabilirsiniz");
    } catch (e) { console.error(e); toast.error("Paylaşım başarısız"); }
  };

  return (
    <Dialog open={!!sale} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[98vw] sm:max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="qs-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary" />Satış Fişi Önizleme
            {sale && <span className="font-mono text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5" data-testid="qs-preview-belge-no">{receiptBelgeNo(sale)}</span>}
          </DialogTitle>
        </DialogHeader>
        {sale && (
          <>
            <div className="flex flex-wrap items-center gap-2 pb-2" data-testid="qs-receipt-cols">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Görünüm:</span>
              {[["code", "Ürün kodu"], ["brand", "Marka adı"], ["discount", "İskonto"], ["logos", "Marka logoları"], ["single", "Tek sayfa"], ["vat", "KDV notu"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setRcols((c) => ({ ...c, [k]: !c[k] }))} data-testid={`qs-rcol-${k}`}
                  className={cn("rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors", rcols[k] ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-muted-foreground hover:border-slate-300")}>{l}</button>
              ))}
              {isAdmin && (
                <div className="ml-auto flex items-center gap-2">
                  <button type="button" onClick={() => setShowPart((v) => !v)} data-testid="qs-preview-part-toggle"
                    className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold transition-colors", showPart ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-muted-foreground hover:border-indigo-300")}>
                    <Users className="h-3.5 w-3.5" />Katılım
                  </button>
                  <button type="button" onClick={() => setShowProfit((v) => !v)} data-testid="qs-preview-profit-toggle"
                    className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold transition-colors", showProfit ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-muted-foreground hover:border-emerald-300")}>
                    {showProfit ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}Kâr / Marj
                  </button>
                </div>
              )}
            </div>
            {rcols.vat && (
              <div className="flex items-center gap-2 pb-2" data-testid="qs-vat-note-row">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">KDV notu:</span>
                <Input value={vatText} onChange={(e) => setVatText(e.target.value)} maxLength={60} placeholder="Ör. KDV uygulanmamıştır" className="h-8 text-sm max-w-sm" data-testid="qs-vat-note-input" />
                <span className="text-[11px] text-muted-foreground hidden sm:inline">Fişte Genel Toplamın altında yazılır</span>
              </div>
            )}
            {isAdmin && showProfit && (() => { const pf = saleProfit(sale); return (
              <div className="mb-2 grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border border-emerald-300 bg-emerald-50/70 p-3" data-testid="qs-preview-profit">
                <Stat2 label="Toplam Maliyet" value={TRY(pf.cost)} testid="qs-preview-profit-cost" />
                <Stat2 label="Satış (net)" value={TRY(pf.net)} testid="qs-preview-profit-rev" />
                <Stat2 label="Kâr" value={TRY(pf.profit)} accent testid="qs-preview-profit-amount" />
                <Stat2 label="Marj" value={`%${NUM(pf.margin)}`} accent testid="qs-preview-profit-margin" />
              </div>
            ); })()}
            {isAdmin && showPart && (
              <div className="mb-2" data-testid="qs-preview-participation">
                <ParticipationInfo sale={sale} testid="qs-preview-participation-info" />
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto rounded-lg bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#cbd5e1_100%)] p-3 sm:p-5" data-testid="qs-preview-body">
              {pdfData ? <PdfPagesView data={pdfData} width={width} testid="qs-preview-pdf" />
                : <div className="flex items-center justify-center py-24 text-sm text-muted-foreground" data-testid="qs-preview-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" />Fiş hazırlanıyor…</div>}
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-3">
              <Button variant="outline" onClick={onClose} data-testid="qs-preview-close"><X className="h-4 w-4 mr-1.5" />Kapat</Button>
              {onSendPrep && !sale.cancelled && <Button variant="secondary" onClick={() => onSendPrep(sale)} data-testid="qs-preview-prep"><ClipboardCheck className="h-4 w-4 mr-1.5" />Hazırlanacak Listeye Gönder</Button>}
              {onToQuote && <Button variant="secondary" onClick={() => onToQuote(sale)} data-testid="qs-preview-quote"><FileSignature className="h-4 w-4 mr-1.5" />Teklife Çevir</Button>}
              <Button variant="secondary" onClick={doShare} disabled={busy || !pdfData} data-testid="qs-preview-share"><Share2 className="h-4 w-4 mr-1.5" />Paylaş</Button>
              <Button variant="secondary" onClick={doDownload} disabled={busy || !pdfData} data-testid="qs-preview-download">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}İndir</Button>
              <Button onClick={doPrint} disabled={busy || !pdfData} data-testid="qs-preview-print"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
